import { userCoursePerformanceId, type User } from '@webapp2/shared/model'
import { computed } from 'vue'
import { localSettings } from '../main'
import { appRoutes, authRoutes, router } from '../router'
import {
  getCookie,
  registeredStateId,
  state,
  type BuildDownloadState,
  type ModuleBuildRegistrationState,
  type VuplexData
} from '../state'
import { logOut } from './authentication-service'
import { cache } from './cache'
import { initialUpdate } from './entity-service'
import { trackEvent } from './log-rocket-service'
import { qrLogin } from './qr-login'
import { handleRunEnd, type RunLog } from './run-log-service'
import { emulateVuplex } from './vuplex-emulator'

export type JoinMultiplayerSessionData = {
  sessionId: string
  address: string
  inviteCode: string
  launchData: LaunchData
}

export type UserData = {
  id: string
  email: string
  displayName?: string
  role: string
  country: string
  isOsso: boolean
}

export function toUserData({ id, email, displayName, role, country, isOsso }: User): UserData {
  return { id, email, displayName, role, country, isOsso }
}

export type LaunchData = {
  build: ModuleBuildLaunchData | EmbeddedModuleLaunchData
  courseId?: string
  isTestMode: boolean
  runTutorial: boolean
}

export type LaunchMessage2Data = { playlist: (SingleLaunchData | MultiplayerLaunchData)[] }

export type MultiplayerLaunchData = {
  type: 'Multiplayer'
  address: string
  inviteCode: string
}

export type SingleLaunchData = {
  type: 'SinglePlayer'
  build: ModuleBuildLaunchData | EmbeddedModuleLaunchData
  courseId?: string
  isTestMode: boolean
  runTutorial: boolean
}

export type ModuleBuildLaunchData = {
  id: string
  techniqueId: string
  techniquePath: string
  uri: string
}

export type EmbeddedModuleLaunchData = {
  techniqueId: string
  techniquePath: string
}

export type AuthenticatedData = {
  user: UserData
  organizationId: string
  userSettings: Record<string, unknown> | null
}

export type RegisterModulesData = {
  builds: RegisterModuleBuildData[]
}

export type RegisterModuleBuildData = {
  id: string
  techniqueId: string
  techniquePath: string
  uri: string
}

export type LaunchMessage = {
  type: 'launch'
  data: LaunchData
}

export type LaunchMessage2 = {
  type: 'launch'
  data: LaunchMessage2Data
}

export type PermissionsData = { accepted: boolean }

export type StartupResponseData = {
  runLog: RunLog
  permissions: PermissionsData
}

type ScanQrCodeMessage = { type: 'read-qr' }
type AuthenticatedMessage = {
  type: 'authenticated'
  data: AuthenticatedData
}
type RegisterModulesMessage = {
  type: 'register-modules'
  data: RegisterModulesData
}
type JoinMultiplayerSessionMessage = {
  type: 'join-multiplayer-session'
  data: JoinMultiplayerSessionData
}
type RequestPermissions = { type: 'request-permissions' }
type StartupMessage = { type: 'startup' }
type ExitApplicationMessage = { type: 'exit-application' }

type MessageToVuplex =
  | ScanQrCodeMessage
  | LaunchMessage
  | LaunchMessage2
  | AuthenticatedMessage
  | RegisterModulesMessage
  | RequestPermissions
  | StartupMessage
  | JoinMultiplayerSessionMessage
  | ExitApplicationMessage

type VuplexBuildDownloadStatus = {
  id: string
  downloaded: boolean
  downloadedBytes: number
  totalBytes: number
  error: boolean
}

type BuildDownloadStatusMessage = { type: 'build-download-status'; data: VuplexBuildDownloadStatus }
type QrCodeReadMessage = { type: 'qr-code'; data: string }
type QrModeCancelledMessage = { type: 'qr-mode-cancelled'; data: undefined }
type LogoutMessage = { type: 'logout'; data: undefined }
type PermissionsMessage = { type: 'permissions'; data: PermissionsData }
type StartupResponseMessage = { type: 'startup-response'; data: StartupResponseData }
type MessageFromVuplex =
  | QrCodeReadMessage
  | BuildDownloadStatusMessage
  | LogoutMessage
  | QrModeCancelledMessage
  | StartupResponseMessage
  | PermissionsMessage

let startupResponseData: StartupResponseData | undefined
let resolveStartUpRequest: (value?: StartupResponseData) => void

export async function initQuestBridge(): Promise<StartupResponseData | undefined> {
  parseVuplexInit()

  if (state.vuplex) {
    addMessageHandler()
    const startupData = new Promise<StartupResponseData | undefined>(resolve => (resolveStartUpRequest = resolve))
    // Wait for the startup response or timeout after 5 seconds (for older builds that don't send a startup response)
    const fiveSecondsInMs = 5000
    postMessage({ type: 'startup' })
    const result = await Promise.race([startupData, new Promise<undefined>(x => setTimeout(() => x, fiveSecondsInMs))])
    return result
  }
  return undefined
}

function parseVuplexInit() {
  state.vuplex = localSettings?.vuplex ?? parseInitCookie()
  state.platform = state.vuplex ? (state.vuplex?.platform ?? 'quest') : 'web'

  if (localSettings?.vuplex || window.testConfig?.emulateVuplex) {
    emulateVuplex()
  }
  console.log(`Vuplex ${state.vuplex ? '' : 'not '}initialized`, state.vuplex)
}

function parseInitCookie() {
  const vuplexCookie = getCookie('vuplex_init')
  return vuplexCookie ? (JSON.parse(vuplexCookie) as VuplexData) : undefined
}

let scanQrTimeout: ReturnType<typeof setTimeout> | undefined

export function scanQrCode() {
  postMessage({ type: 'read-qr' })
  scanQrTimeout = setTimeout(() => void router.push(authRoutes.verifyQrCode), 200)
}

export function authenticated({
  user: { id, email, displayName, role, country, isOsso },
  organizationId,
  userSettings
}: AuthenticatedData) {
  postMessage({
    type: 'authenticated',
    data: { user: { id, email, displayName, role, country, isOsso }, organizationId, userSettings }
  })
}

export function launch(data: LaunchData) {
  trackEvent('launch', data)
  postMessage({ type: 'launch', data })
}

export function join(data: JoinMultiplayerSessionData) {
  trackEvent('join', data)
  postMessage({
    type: 'join-multiplayer-session',
    data
  })
}

export function launch2(data: LaunchMessage2Data) {
  trackEvent(
    'launch',
    Object.fromEntries(data.playlist.flatMap((x, i) => Object.entries(x).map(([key, value]) => [`${key}_${i}`, value])))
  )
  postMessage({ type: 'launch', data })
}

export function registerModules(data: RegisterModulesData) {
  postMessage({ type: 'register-modules', data })
}

export function requestPermissions() {
  postMessage({ type: 'request-permissions' })
}

export function exitApplication() {
  postMessage({ type: 'exit-application' })
}

function postMessage(message: MessageToVuplex) {
  if (!window.vuplex) {
    return
  }
  console.log('postMessage', message)
  window.vuplex.postMessage(message)
}

async function handleBuildDownloadStatus(message: VuplexBuildDownloadStatus) {
  if (message.error) {
    console.log(`Error while downloading module build: ${message.id}`)
    return
  }
  const buildDownloadState: BuildDownloadState = message.error
    ? 'Error'
    : message.downloaded
      ? 'Downloaded'
      : message.downloadedBytes > 0
        ? 'Downloading'
        : 'Queued'
  const buildDownloadStatus = { ...message, state: buildDownloadState }
  const build = state.moduleBuilds[buildDownloadStatus.id]
  if (!build) {
    throw new Error(`No build found for download status, this should not happen. Build ID: ${buildDownloadStatus.id}`)
  }
  const id = registeredStateId(build.techniquePath)
  const registeredState: ModuleBuildRegistrationState = {
    id,
    type: 'RegisteredState',
    techniquePath: build.techniquePath,
    moduleBuildId: build.id,
    downloadStatus: buildDownloadStatus,
    inMemory: false,
    isOverridden: state.registeredStates[id]?.isOverridden ?? false,
    overrideId: state.registeredStates[id]?.overrideId !== build.id ? state.registeredStates[id]?.overrideId : undefined
  }
  if (buildDownloadState === 'Downloaded') {
    await cache.set(id, registeredState)
  }
  registeredState.inMemory = true
  state.registeredStates[id] = registeredState
}

function handleStartupData(startupData?: StartupResponseData) {
  console.log('Startup response received', startupData)
  state.permissionsAccepted = startupData?.permissions?.accepted ?? true
  startupResponseData = startupData
  resolveStartUpRequest(startupData)
}

export async function getRunEndRoute(path?: string) {
  const runId = startupResponseData?.runLog?.id
  if (!startupResponseData || !runId) {
    return
  }
  await initialUpdate
  await handleRunEnd(startupResponseData.runLog)
  const run = runId ? state.runResults[runId] : undefined
  if (!run) {
    return
  }
  if (path && path !== '/') {
    return
  }
  const isCollab = Boolean(run?.multiplayerSessionId)
  // if the user left collab from the user panel show the join page
  if (isCollab && !run.exitModule) {
    const route = run.courseId
      ? {
          name: 'join',
          params: {
            organizationId: state.organizationId,
            id: run?.multiplayerSessionId
          }
        }
      : { name: 'admin-multiplayer' }
    return route
  }
  const perf = run.courseId
    ? state.userCoursePerformances[userCoursePerformanceId(state.organizationId!, run.courseId, state.user!.id)]
    : undefined
  let count = perf?.totalRunCount ?? 0
  if (perf?.recentRuns?.at(-1)?.id !== runId && run.completed) {
    count++
  }
  const name =
    !state.user?.isOsso && !isCollab && run.completed && (count === 1 || count == 3)
      ? 'debrief-survey'
      : 'debrief-overview'
  return {
    name,
    params: {
      organizationId: state.organizationId,
      runId
    }
  }
}

async function handlePermissions(data: PermissionsData) {
  state.permissionsAccepted = data.accepted
  const isPermissionDeniedPage = router.currentRoute.value.name === authRoutes.permissionsDenied.name
  if (data.accepted && isPermissionDeniedPage) {
    await router.push(appRoutes.root)
  }
  if (!data.accepted && !isPermissionDeniedPage) {
    await router.push(authRoutes.permissionsDenied)
  }
}

function addMessageHandler() {
  window.addEventListener('vuplexmessage', event => {
    const messageEvent = event as MessageEvent
    const message = parseMessage(messageEvent.data as string)
    console.log('Message from Vuplex', message)

    switch (message.type) {
      case 'qr-code':
        clearTimeout(scanQrTimeout)
        void qrLogin(message.data)
        break
      case 'qr-mode-cancelled':
        clearTimeout(scanQrTimeout)
        void router.push(authRoutes.login)
        break
      case 'build-download-status':
        void handleBuildDownloadStatus(message.data)
        break
      case 'startup-response':
        handleStartupData(message.data)
        break
      case 'logout':
        void logOut()
        break
      case 'permissions':
        void handlePermissions(message.data)
        break
      default:
        console.error('Invalid vuplex message', message)
    }
  })
}

function parseMessage(message: string) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const parsedMessage = JSON.parse(message)
  if (isVuplexMessage(parsedMessage)) {
    console.log('Vuplex message received', parsedMessage)
    return parsedMessage
  }
  throw new Error(`Invalid vuplex message ${message}`)
}

function isVuplexMessage(obj: unknown): obj is MessageFromVuplex {
  return typeof obj === 'object' && obj !== null && 'type' in obj
}

export const isQuest = computed<boolean>(() => state.vuplex !== undefined)
